<template>
  <VueApexCharts 
      v-show="streamAlive && isAllowed" 
      ref="chart" :options="options" 
      :series="series"
      type="line"
      @mouseMove="onMouseMove"
      @mouseLeave="mouseLeave"
      @legendClick="onLegendClick"
    >
  </VueApexCharts>
  <div v-if="!streamAlive || !isAllowed" class="offline-banner">
    <icon-base class="text-surface-4" width="40" height="40" width-view-box="40" height-view-box="40">
      <icon-offline />
    </icon-base>
    <div v-if="!isAllowed" class="mt-4">
      <p class="mb-0 text-s-m"><a target="_blank" class="text-reset text-underline" href="/app/subscribe">Upgrade</a> plan to view this statistic.</p>
    </div>
    <div class="mt-4" v-else>You're offline</div>
  </div>
  <div class="chart-tooltip" v-show="showTooltip" v-follow-tooltip="'apexcharts-tooltip'" :style="tooltipStyle">
    <div class="series-1">
      <span class="apexcharts-legend-marker" rel="1" data:collapsed="false" style="background: rgb(32, 118, 255); color: rgb(32, 118, 255); height: 12px; width: 12px; left: 0px; top: 0px; border-width: 0px; border-color: rgb(255, 255, 255); border-radius: 4px;"></span>
      <span>Avg. Bitrate</span>
      <span class="tooltip-value">{{ tooltipAvgBitrate }}</span>
    </div>
    <div class="series-2">
      <span class="apexcharts-legend-marker" rel="2" data:collapsed="false" style="background: rgb(240, 111, 157); color: rgb(240, 111, 157); height: 12px; width: 12px; left: 0px; top: 0px; border-width: 0px; border-color: rgb(255, 255, 255); border-radius: 4px;"></span>
      <span>Bitrate</span>
      <span class="tooltip-value">{{ tooltipBitrate }}</span>
    </div>
  </div>
</template>
<script>
import VueApexCharts from "vue3-apexcharts";
import moment from "moment";
import _ from "lodash";
import IconBase from "@/components/icon/IconBase.vue";
import IconOffline from "@/components/icon/IconOffline.vue";

export default {
  name: "MonitoringChart",
  components: {
    VueApexCharts,
    IconBase,
    IconOffline,
  },
  props: {
    streamKey: {
      type: String,
      default: "",
    },
    streamAlive: {
        type: Boolean,
        default: false,
    },
    isAllowed: {
      type: Boolean,
      default: false,
    },
    legendPosition: {
      type: String,
      default: "top",
    },
    tooltip: {
      type: Boolean,
      default: true,
    },
  },
  mounted() {
    console.log('monitoring chart mounted');
    if(this.isAllowed) {
      this.wsConnect();
      // setTimeout(() => {
      //   this.wsDisconnect();
      // }, 5000)
    }
  },
  beforeUnmount () {
    console.log('monitoring chart destroyed');
    this.wsDisconnect();
  },
  data() {
    return {
      options: {
        chart: {
          id: 'monitoring-chart',
          type: 'line',
          animations: {
            enabled: true,
            easing: 'linear',
            dynamicAnimation: {
              enabled: true,
              speed: 1000
            }
          },
          toolbar: {
            show: false
          },
          zoom: {
            enabled: false,
          },
        },
        legend: {
          position: this.legendPosition,
          fontSize: '12px',
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",      
          horizontalAlign: 'left',
          labels: {
            colors: ["#8794B5", "#8794B5"],
          },
          markers: {
              size: 4,
              shape: 'square', // circle, square, line, plus, cross
              radius: 4,
          },
        },
        xaxis: {
          type: 'datetime',
          range: 60000,
          stepSize: 30000,
          labels: {
            datetimeUTC: false,
            showDuplicates: false,
            style: {
                colors: "#8794B5",
                fontSize: '12px',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
                fontWeight: 400,
                cssClass: 'apexcharts-yaxis-label',
            },
          },  
        },
        yaxis: {
          decimalsInFloat: 0,
          min: 0,
          title: {
            text: 'Bitrate (kbps)',
            style: {
                color: "#8794B5",
                fontSize: '12px',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
                fontWeight: 400,
                cssClass: 'apexcharts-yaxis-label',
            },
          },
          labels: {
            style: {
                colors: ["#8794B5"],
                fontSize: '12px',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'",
                fontWeight: 400,
                cssClass: 'apexcharts-yaxis-label',
            },
          },
        },
        tooltip: {
          enabled: this.tooltip,
          theme: 'dark',
        },
        grid: {
          show: false,
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          curve: 'smooth',
          width: [1, 1],
        },
        fill: {
          type:'solid',
          opacity: [0.35, 0.35],
        },
      },
      series: [{
        name: 'Avg Bitrate',
        type: 'area',
        color: '#2076FF',
        data: []
      }, {
        name: 'Bitrate',
        color: '#F06F9D',
        type: 'area',
        data: []
      }],
      avgBitrateData: [],
      bitrateData: [],
      retryCounter: 0,
      movingAverage: 0,
      pointCounter: 0,
      refreshCounter: 0,
      showTooltip: false,
      isAvgBitrateHidden: false,
      isBitrateHidden: false,
      tooltipPosition: {
        x: 0,
        y: 0,
      },
      tooltipStyle: {
        position: 'absolute',
        top: '0px',
        left: '0px',
      },
      tooltipAvgBitrate: 0,
      tooltipBitrate: 0,
    };
  },
  methods: {
    dataPointMouseEnter(event, chartContext, opts) {
    },
    onMouseMove(event, chartContext, opts) {
      if(opts.dataPointIndex < 0) return;
      this.showTooltip = true;
      if(this.series[0]?.data[opts?.dataPointIndex]?.[1]) this.tooltipAvgBitrate = this.series[0]?.data[opts.dataPointIndex]?.[1]?.toFixed(0);
      if(this.series[1]?.data[opts?.dataPointIndex]?.[1]) this.tooltipBitrate = this.series[1].data[opts.dataPointIndex]?.[1]?.toFixed(0);
    },
    onLegendClick(chartContext, seriesIndex, opts) {
      console.log(this.$refs.chart, chartContext);
      
      if(seriesIndex === 0) {
        this.isAvgBitrateHidden = !this.isAvgBitrateHidden;
      } else if(seriesIndex === 1) {
        this.isBitrateHidden = !this.isBitrateHidden;
      }
    },
    mouseLeave(event, chartContext, opts) {
      this.showTooltip = false;
    },
    wsConnect() {
        if(!this.streamKey) return;
        this.ws = new WebSocket(`wss://stats.castr.io/beats/${this.streamKey}`);
        this.ws.onopen = () => {
            console.log('WebSocket connection established');
            this.retryCounter = 0;
            this.pointCounter = 0;
            this.movingAverage = 0;
        }

        this.ws.onmessage = this.onMessage.bind(this);

        this.ws.onerror = (err) => {
            console.log('[SOCK_ERR]', err);
            this.ws.close();
        }

        this.ws.onclose = () => {
            console.log('[SOCK]: connection closed.');
            this.ws = null;
        }
    },
    wsDisconnect() {
        if(this.ws) {
            this.ws.close?.();
        }
    },
    onMessage() {
        if(typeof event.data !== 'string') return;
        const data = JSON.parse(event.data);
        if (data.message === "Stream not found") {
            console.log('Stream not found, retrying...');
            this.retryConnection();
        } else if (data.data) {
            if(data.data.length) {
                this.parseData(data.data);
            }
        }
    },
    retryConnection() {
        if(this.retryCounter >= 10) return;
        this.retryCounter++;
        setTimout(this.wsConnect, 500 * this.retryCounter);
    },
    parseData(data) {
        let raw = [];
        let avg = [];

        for (const point of data) {
            const ts = point[0];
            if(this.series[1].data.length && ts <= this.series[1].data[this.series[1].data.length - 1][0]) continue;

            const bitrate = point[1] / 1024;
            this.pointCounter++;

            this.movingAverage = this.movingAverage + (bitrate - this.movingAverage) / this.pointCounter;

            raw.push([ts, bitrate]);
            avg.push([ts, this.movingAverage]);
        }

        // this.series[0].data.push(...avg);
        // this.series[1].data.push(...raw);



        const index = this.series[1].data.length - 1;
        if(index >= 0) {
          const min = 60000;
          const trigger = this.series[1].data[index][0] - min * 2;
          const oneMinAgo = this.series[1].data[index][0] - min;
          if(this.series[1].data.length && this.series[1].data[0][0] < trigger) {
            const avgData = this.series[0].data.slice().filter(x => x[0] >= oneMinAgo);
            const rawData = this.series[1].data.slice().filter(x => x[0] >= oneMinAgo);
            this.series[0].data = avgData;
            this.series[1].data = rawData;
          }
        }
        this.series[0].data.push(...avg);
        this.series[1].data.push(...raw);

        if(this.isAvgBitrateHidden) {
          this.$nextTick(() => {
            this.options.chart.animations.enabled = false;
            this.$refs.chart.hideSeries(this.series[0].name);
            this.$nextTick(() => {
              this.options.chart.animations.enabled = false;
            })
          });
        }

        if(this.isBitrateHidden) {
          this.$nextTick(() => {
            this.options.chart.animations.enabled = false;
            this.$refs.chart.hideSeries(this.series[1].name);
            this.$nextTick(() => {
              this.options.chart.animations.enabled = false;
            })
          })
        }
    }
  },
  computed: {
  },
  directives: {
    followTooltip: {
      mounted(el, binding) {
        const tooltipClass = binding.value;
        const instance = binding.instance;

        const updateTooltipPosition = () => {
          const thirdPartyTooltip = document.querySelector(`.${tooltipClass}`);
          if (thirdPartyTooltip) {
            const rect = thirdPartyTooltip.getBoundingClientRect();
            if(Array.from(thirdPartyTooltip.classList).includes('apexcharts-active') && rect.top && rect.left) {
              instance.tooltipStyle.top = `${rect.top + window.scrollY}px`;
              instance.tooltipStyle.left = `${rect.left + window.scrollX}px`;
            }
          }
        };

        el._interval = setInterval(updateTooltipPosition, 10);
      },
      unmounted(el) {
        clearInterval(el._interval);
      },
    },
  },
  watch: {
    isAllowed(newState) {
      if(newState && !this.ws) {
        this.wsConnect();
      }
    }
  },
};
</script>
<style>
#container {
  height: 350px;
}

.offline-banner {
    width: 100%;
    height: 200px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    color: var(--c-dark-8);
    padding: 0;
}

.apexcharts-tooltip {
    background: var(--surface-2);
    color: var(--c-dark-8);
    opacity: 0 !important;
  }

 .chart-tooltip {
    /* background: var(--surface-2); */
    background: rgba(0, 0, 0, 0.7);
    color: var(--c-dark-8);
    font-size: 12px;
    padding: 8px;
 }

 .tooltip-value {
    font-weight: bold;
    margin-left: 8px;
 }
</style>